import React, { useState, useEffect } from "react";
import { X } from "lucide-react";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const inputCls = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50 transition-colors";
const labelCls = "text-xs text-[#00ff90]/50 uppercase tracking-wider block mb-1.5";

const EVAL_METHODS = [
  { key: "KRA/KPI", label: "KRA / KPI",     desc: "Weighted goal ratings 1–5"                          },
  { key: "OKR",     label: "OKR",            desc: "Objectives + measurable key results (0–1 score)"    },
  { key: "360",     label: "360 Feedback",   desc: "Peer + subordinate + manager ratings"               },
  { key: "MBO",     label: "MBO",            desc: "Management by objectives, completion %"             },
  { key: "BARS",    label: "BARS",           desc: "Behaviourally anchored competency ratings"          },
  { key: "Bell",    label: "Bell Curve",     desc: "Forced distribution overlay on final scores"        },
];

const STAGES = ["Goal Setting","Self","Mgr Review","Peer","360","Calibration","Finalize"];

function ratingColor(r) {
  if (!r) return "rgba(255,255,255,0.3)";
  if (r >= 4.5) return "#00ff90";
  if (r >= 3.5) return "#60a5fa";
  if (r >= 2.5) return "#fbbf24";
  return "#f87171";
}

export default function ReviewDrawer({ open, row, onClose, onSetRating, onMoveStage }) {
  const [tab,        setTab]        = useState("overview");
  const [rating,     setRating]     = useState("");
  const [nextStage,  setNextStage]  = useState("Finalize");
  const [method,     setMethod]     = useState("");
  const [saving,     setSaving]     = useState(false);
  const [methodMsg,  setMethodMsg]  = useState(null);

  useEffect(() => {
    if (row) {
      setRating(row.rating || "");
      setMethod(row.method || "");
    }
  }, [row?.id]);

  if (!open || !row) return null;

  const saveRating = async () => {
    setSaving(true);
    try { await onSetRating?.(row.id, Number(rating)); }
    finally { setSaving(false); }
  };

  const saveMethod = async () => {
    setSaving(true); setMethodMsg(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API}/api/admin/performance/review/${row.id}/method`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ method }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setMethodMsg("✓ Evaluation method saved.");
    } catch (e) { setMethodMsg(`⚠ ${e.message}`); }
    finally { setSaving(false); }
  };

  const color = ratingColor(row.rating);

  return (
    <div className="fixed inset-0 z-[10070]">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full sm:w-[500px] bg-[#0d0d0d] border-l border-[#00ff90]/20 shadow-[-20px_0_60px_#00ff9011] text-white flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/5 shrink-0">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <p className="font-semibold text-white">{row.name}</p>
              <span className="text-xs text-white/30">{row.empCode}</span>
              {row.method && (
                <span className="text-xs px-2 py-0.5 rounded-full border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60">
                  {row.method}
                </span>
              )}
            </div>
            <p className="text-xs text-[#00ff90]/50">{row.dept} · {row.manager}</p>
          </div>
          <div className="flex items-center gap-3">
            {row.rating != null && (
              <div className="text-right">
                <p className="text-lg font-bold" style={{ color }}>{parseFloat(row.rating).toFixed(1)}</p>
                <p className="text-xs text-white/30">Rating</p>
              </div>
            )}
            <button onClick={onClose}
              className="w-8 h-8 rounded-lg border border-white/10 bg-white/5 flex items-center justify-center text-white/40 hover:text-white transition-colors">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 px-5 py-3 border-b border-white/5 shrink-0">
          {[["overview","Overview"],["method","Eval Method"],["actions","Actions"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                tab === k
                  ? "bg-[#00ff90]/15 border-[#00ff90]/40 text-[#00ff90]"
                  : "bg-transparent border-white/10 text-white/40 hover:border-[#00ff90]/20 hover:text-white/60"
              }`}>{l}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">

          {/* Overview tab */}
          {tab === "overview" && (
            <>
              <div className="rounded-xl border border-white/5 bg-white/3 divide-y divide-white/5">
                {[
                  ["Department",    row.dept],
                  ["Location",      row.location],
                  ["Manager",       row.manager],
                  ["Stage",         row.stage],
                  ["Eval Method",   row.method || "Not assigned"],
                  ["Goals Progress",`${Math.round((row.goalsProgress || 0) * 100)}%`],
                  ["Potential",     row.potential || "—"],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between px-4 py-2.5">
                    <span className="text-xs text-[#00ff90]/50 uppercase tracking-wider">{k}</span>
                    <span className="text-sm text-white/70">{v}</span>
                  </div>
                ))}
              </div>

              {/* Goals progress bar */}
              <div className="rounded-xl border border-white/5 bg-white/3 p-4">
                <p className="text-xs text-[#00ff90]/50 uppercase tracking-wider mb-3">Goals Progress</p>
                <div className="h-2 rounded-full bg-white/5">
                  <div className="h-full rounded-full bg-[#00ff90] transition-all"
                    style={{ width: `${Math.round((row.goalsProgress || 0) * 100)}%` }} />
                </div>
                <p className="text-xs text-white/30 mt-1">{Math.round((row.goalsProgress || 0) * 100)}% complete</p>
              </div>
            </>
          )}

          {/* Evaluation Method tab */}
          {tab === "method" && (
            <div className="space-y-4">
              <p className="text-xs text-white/40 leading-relaxed">
                Select the evaluation method for this employee. This determines which form they see during the review cycle.
                The method can be inherited from their assigned Performance Profile or set individually here.
              </p>

              <div className="space-y-2">
                {EVAL_METHODS.map(({ key, label, desc }) => (
                  <button key={key} onClick={() => setMethod(key)}
                    className={`w-full text-left rounded-xl border p-4 transition-all ${
                      method === key
                        ? "border-[#00ff90]/40 bg-[#00ff90]/8"
                        : "border-white/5 bg-white/3 hover:border-[#00ff90]/20 hover:bg-[#00ff90]/5"
                    }`}>
                    <div className="flex items-center justify-between mb-0.5">
                      <span className={`text-sm font-medium ${method === key ? "text-[#00ff90]" : "text-white/70"}`}>{label}</span>
                      {method === key && <span className="text-xs text-[#00ff90]/60">Selected</span>}
                    </div>
                    <p className="text-xs text-white/30">{desc}</p>
                  </button>
                ))}
              </div>

              {methodMsg && <p className={`text-xs ${methodMsg.startsWith("⚠") ? "text-red-400" : "text-[#00ff90]"}`}>{methodMsg}</p>}

              <button onClick={saveMethod} disabled={saving || !method}
                className="w-full text-xs py-2.5 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors font-medium">
                {saving ? "Saving…" : "Save Evaluation Method"}
              </button>
            </div>
          )}

          {/* Actions tab */}
          {tab === "actions" && (
            <div className="space-y-5">
              {/* Set rating */}
              <div>
                <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-3">Set / Override Rating</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className={labelCls}>Rating (1.0 – 5.0)</label>
                    <input type="number" step="0.1" min="1" max="5" className={inputCls}
                      value={rating} onChange={e => setRating(e.target.value)} />
                  </div>
                  <button onClick={saveRating} disabled={saving || !rating}
                    className="text-xs px-4 py-2 rounded-lg bg-[#00ff90]/15 text-[#00ff90] border border-[#00ff90]/30 hover:bg-[#00ff90]/25 disabled:opacity-40 transition-colors self-end">
                    {saving ? "…" : "Save"}
                  </button>
                </div>
                {rating && (
                  <div className="mt-2 w-16 h-16 rounded-2xl border flex items-center justify-center"
                    style={{ borderColor: `${ratingColor(Number(rating))}40`, background: `${ratingColor(Number(rating))}10` }}>
                    <span className="text-xl font-bold" style={{ color: ratingColor(Number(rating)) }}>
                      {parseFloat(rating).toFixed(1)}
                    </span>
                  </div>
                )}
              </div>

              {/* Move stage */}
              <div>
                <p className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium mb-3">Move Stage</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1">
                    <label className={labelCls}>Target Stage</label>
                    <select className={inputCls} value={nextStage} onChange={e => setNextStage(e.target.value)}>
                      {STAGES.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <button onClick={() => onMoveStage?.(row.id, nextStage)} disabled={saving}
                    className="text-xs px-4 py-2 rounded-lg bg-blue-500/15 text-blue-400 border border-blue-500/30 hover:bg-blue-500/25 disabled:opacity-40 transition-colors self-end">
                    Move
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}